// ============================================================================
// Deep Link Generator — searchable picker for a detail page's target row
// ============================================================================
// Replaces "paste a UUID" with "find the thing". The component owns nothing but
// the popover and the search box: the SELECTED VALUE is still just the id
// string held by the parent, so everything downstream of it (buildDeepLinkUrl,
// the preview, the QR) is untouched by this existing.
//
// The manual-entry escape hatch is deliberate, not a leftover. An admin may
// have an id from a support ticket for a row that is archived, outside the
// result cap, or in a table this picker cannot reach — and if a lookup query
// ever fails, the generator must still be usable. So the raw input stays one
// click away and the parent treats both paths identically.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2, Search, X, Keyboard, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { isUuid, type DeepLinkEntityOption, type DeepLinkEntitySource } from '@/lib/deepLinkEntities';

/** How many rows one search returns. Enough to scroll, small enough to stay instant. */
const RESULT_LIMIT = 25;

/** Colour the status pill by meaning, not by table — the words overlap across
 *  campaigns, jobs and offers, so one map covers all three. */
const STATUS_TONE: Record<string, string> = {
  active: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  confirmed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  completed: 'border-blue-200 bg-blue-50 text-blue-700',
  invited: 'border-amber-200 bg-amber-50 text-amber-700',
  waitlisted: 'border-amber-200 bg-amber-50 text-amber-700',
  paused: 'border-amber-200 bg-amber-50 text-amber-700',
  declined: 'border-red-200 bg-red-50 text-red-700',
  failed: 'border-red-200 bg-red-50 text-red-700',
  expired_offer: 'border-red-200 bg-red-50 text-red-700',
  archived: 'border-gray-200 bg-gray-50 text-gray-600',
};

function StatusPill({ status }: { status?: string | null }) {
  if (!status) return null;
  return (
    <Badge
      variant="outline"
      className={cn('shrink-0 text-[10px] font-medium capitalize', STATUS_TONE[status] ?? 'text-muted-foreground')}
    >
      {status.replace(/_/g, ' ')}
    </Badge>
  );
}

/** Thumbnail, or a monogram tile when the row has no image. */
function Thumb({ option }: { option: DeepLinkEntityOption }) {
  if (option.imageUrl) {
    return (
      <img
        src={option.imageUrl}
        alt=""
        className="h-9 w-9 shrink-0 rounded-md border object-cover"
        loading="lazy"
      />
    );
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-sm font-semibold text-primary">
      {(option.title || '?').charAt(0).toUpperCase()}
    </div>
  );
}

export interface DeepLinkEntityPickerProps {
  /** Where to search. Changing it resets the picker. */
  source: DeepLinkEntitySource;
  /** Identity of the source, so the query cache and the reset effect key off it. */
  sourceKey: string;
  /** The selected row id — '' when nothing is chosen. The single source of truth. */
  value: string;
  onChange: (id: string) => void;
  /** Placeholder for the manual-entry input, from the page registry. */
  idPlaceholder?: string;
}

export function DeepLinkEntityPicker({
  source, sourceKey, value, onChange, idPlaceholder,
}: DeepLinkEntityPickerProps) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [manual, setManual] = useState(false);
  // The row behind `value`, for the closed-state label. Held here rather than
  // lifted so the parent keeps dealing in plain ids.
  const [selected, setSelected] = useState<DeepLinkEntityOption | null>(null);

  // Switching page kind invalidates everything this component knows.
  useEffect(() => {
    setTerm(''); setDebounced(''); setSelected(null); setManual(false); setOpen(false);
  }, [sourceKey]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const { data: results = [], isFetching, isError, error } = useQuery({
    queryKey: ['deep-link-entities', sourceKey, debounced],
    queryFn: () => source.search(debounced, RESULT_LIMIT),
    // Only search while the list is on screen — opening the page shouldn't hit
    // three tables for a picker the admin may never open.
    enabled: open && !manual,
    staleTime: 30_000,
    // Keeps the previous results on screen while a new term is in flight, so
    // the list doesn't blank out on every keystroke.
    placeholderData: keepPreviousData,
  });

  // An id can arrive without going through the list (manual entry, or a value
  // restored from the parent). Resolve it once so the closed state shows a name
  // instead of a bare UUID. A miss is not an error — the id may be valid for a
  // row this picker's query can't see, and the link is still perfectly good.
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (selected?.id === value) return;
    let cancelled = false;
    void (async () => {
      try {
        const found = await source.byId(value);
        if (!cancelled && found) setSelected(found);
      } catch { /* keep showing the raw id */ }
    })();
    return () => { cancelled = true; };
  }, [value, selected?.id, source]);

  const handleSelect = (option: DeepLinkEntityOption) => {
    setSelected(option);
    onChange(option.id);
    setOpen(false);
  };

  const clear = () => { setSelected(null); onChange(''); };

  const emptyLabel = useMemo(() => {
    if (isFetching) return 'Searching…';
    // Say "active" only where the list is actually narrowed to active rows
    // (campaigns and jobs). Offers are unfiltered, and claiming otherwise would
    // send someone hunting for a filter that isn't there.
    const scope = source.listNote ? `active ${source.noun}` : source.noun;
    return debounced ? `No ${scope} match "${debounced}"` : `No ${scope}`;
  }, [isFetching, debounced, source.noun, source.listNote]);

  // ── Manual entry: the same field this control replaced ────────────────────
  if (manual) {
    return (
      <div className="space-y-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={idPlaceholder}
          autoComplete="off"
          className="font-mono text-sm"
          autoFocus
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {value.trim() && !isUuid(value)
              ? "That doesn't look like an ID — the link will still build, but check it before sharing."
              : 'Pasting an ID directly. The link builds exactly the same way.'}
          </p>
          <button
            type="button"
            className="shrink-0 text-xs font-medium text-primary hover:underline"
            onClick={() => setManual(false)}
          >
            Search instead
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-auto w-full justify-between px-3 py-2 font-normal"
          >
            {selected ? (
              <span className="flex min-w-0 items-center gap-3 text-left">
                <Thumb option={selected} />
                <span className="min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{selected.title}</span>
                    {/* Repeated here, not just in the list: a row reached by
                        pasting an ID never passed through the active-only
                        filter, so this is the only place its status shows. */}
                    <StatusPill status={selected.status} />
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {selected.subtitle || selected.id}
                  </span>
                </span>
              </span>
            ) : value ? (
              // An id with no resolvable row — still a valid link, so show it
              // plainly rather than pretending nothing is selected.
              <span className="truncate font-mono text-sm">{value}</span>
            ) : (
              <span className="text-muted-foreground">Select a {source.noun.replace(/s$/, '')}…</span>
            )}
            <span className="ml-2 flex shrink-0 items-center gap-1">
              {value && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Clear selection"
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); clear(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); clear(); } }}
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
              <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
            </span>
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          {/* shouldFilter=false: results are already filtered by Postgres, and
              cmdk's built-in matcher would re-filter them against the raw term
              and hide legitimate server-side matches (e.g. a brand-name hit
              that never appears in the rendered title). */}
          <Command shouldFilter={false}>
            <div className="flex items-center gap-2 border-b px-3">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={source.searchPlaceholder}
                className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                autoFocus
              />
              {isFetching && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />}
            </div>
            <CommandList className="max-h-[300px]">
              {isError ? (
                <div className="flex items-start gap-2 px-3 py-6 text-sm text-muted-foreground">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <span>
                    Couldn't load {source.noun} ({(error as any)?.message || 'unknown error'}).
                    Use <button type="button" className="font-medium text-primary hover:underline" onClick={() => { setOpen(false); setManual(true); }}>manual ID entry</button> instead.
                  </span>
                </div>
              ) : results.length === 0 ? (
                <CommandEmpty className="py-6 text-sm">{emptyLabel}</CommandEmpty>
              ) : (
                <CommandGroup>
                  {results.map((option) => (
                    <CommandItem
                      key={option.id}
                      value={option.id}
                      onSelect={() => handleSelect(option)}
                      className="flex items-center gap-3 px-3 py-2.5"
                    >
                      <Thumb option={option} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{option.title}</span>
                          <StatusPill status={option.status} />
                        </div>
                        {option.subtitle && (
                          <p className="truncate text-xs text-muted-foreground">{option.subtitle}</p>
                        )}
                        <p className="truncate font-mono text-[10px] text-muted-foreground/70">{option.id}</p>
                      </div>
                      {value === option.id && <Check className="h-4 w-4 shrink-0 text-primary" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
            {source.listNote && !isError && (
              <div className="border-t px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
                {source.listNote}
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        onClick={() => setManual(true)}
      >
        <Keyboard className="h-3 w-3" />
        Enter an ID manually
      </button>
    </div>
  );
}
